/**
 * Built-in roles that must never be edited, deleted, or reassigned.
 *
 * This is the single source of truth. It previously existed as four separate
 * copies (RolesList, RoleDetails desktop, RoleDetails phone action bar,
 * UserDetails), each probing a different subset of the backend's markers and
 * each hardcoding the same two names — so the built-in "Main Admin" role,
 * which matches neither "admin" nor "vendoradmin", was editable by any member
 * holding the role:update permission.
 *
 * The backend does not document a flag for this: GET /merchant/roles has no
 * response schema in the OpenAPI spec, and the call sites had accumulated
 * three different guesses (`isSystem`, `system`, `scope === "SYSTEM"`). All
 * three are honoured here, with the name list as the fallback that actually
 * fires today. If the backend later commits to a flag, delete the name list —
 * matching on display names is inherently fragile and is what caused this bug.
 */

/** Role shape is deliberately loose — call sites pass raw API records. */
export interface ProtectedRoleInput {
  name?: string | null;
  title?: string | null;
  isSystem?: boolean | null;
  /** Legacy alias some responses use instead of `isSystem`. */
  system?: boolean | null;
  scope?: string | null;
}

/** Lowercase, and drop the separators that vary between API and UI copy. */
export function normalizeRoleName(name?: string | null): string {
  return (name ?? "").toLowerCase().replace(/[\s_-]/g, "");
}

/**
 * Exact normalized names of the built-in full-access roles.
 *
 * Matched exactly, never as a substring: "Admin Assistant" and
 * "Regional Admin" are roles a merchant can legitimately create and must stay
 * editable.
 */
const PROTECTED_ROLE_NAMES: ReadonlySet<string> = new Set([
  "admin",
  "vendoradmin",
  "mainadmin",
  "owner",
]);

export function isProtectedRole(
  role?: ProtectedRoleInput | null,
): boolean {
  if (!role) return false;
  if (role.isSystem === true || role.system === true) return true;
  if (normalizeRoleName(role.scope) === "system") return true;
  return PROTECTED_ROLE_NAMES.has(normalizeRoleName(role.name ?? role.title));
}
